/**
 * Map layer management.
 */

import type {
	AddLayerObject,
	ExpressionSpecification,
	FilterSpecification,
	GeoJSONSourceSpecification,
	Map as MapLibreMap,
	MapGeoJSONFeature,
	RasterSourceSpecification,
	VectorSourceSpecification
} from 'maplibre-gl';
import type { LineString } from 'geojson';
import type { TContextFeature } from '@safesystem/geoserver-api';
import { contextLayerId } from '#lib/config.js';
import type { ILayerSource, ISystemLayers } from '#lib/types.js';
import type { MapLayer } from '#lib/stores/legendStore.svelte.js';
import { getCenter, getBounds } from '#lib/featureGeometry.js';
import basemapRoadLayers from '#lib/common/layer_styles/context_link_basemap-roads_style.json';
import { getLogger } from '@logtape/logtape';

const log = getLogger(['platform', 'map', 'layers']);

/**
 * Flatten ISystemLayers into a zIndex-sorted array ready for addMapLayers.
 */
export function orderLayers(systemData: ISystemLayers): ILayerSource[] {
	return Object.values(systemData)
		.flatMap((module) => Object.values(module ?? {}))
		.filter((feature) => !!feature)
		.sort((a, b) => a.zIndex - b.zIndex);
}

/**
 * Add all vector tile + GeoJSON sources and layers to the map.
 * Layers default to visible; `syncVisibleLayers` is then driven from
 * `legendStore.layers` (see Map.svelte) to apply per-layer visibility.
 * Returns a map of MapLibre layerId → internal LayerId for event handling.
 */
export function addMapLayers(
	map: MapLibreMap,
	orderedFeatures: ILayerSource[]
): Record<string, string> {
	const layerSpec: Record<string, string> = {};

	for (const feature of orderedFeatures) {
		if (!feature?.layer?.source || !feature.layer.id) continue;
		const sourceId = feature.layer.source as string;
		try {
			// Avoid duplicate sources (e.g. CurveAdvisorySigns + Speeds share same source)
			if (!map.getSource(sourceId)) {
				map.addSource(
					sourceId,
					feature.source as VectorSourceSpecification | GeoJSONSourceSpecification
				);
			}
			// Hover cursor is set by Map.svelte's mousemove handler.
			map.addLayer(feature.layer as AddLayerObject);
			if (feature.selectable !== false) layerSpec[feature.layer.id] = feature.id;
		} catch (err) {
			const error = err instanceof Error ? err.message : String(err);
			log.error('Failed to add layer {layerId}: {error}', { layerId: feature.layer.id, error });
		}
	}

	log.info('Added {layerCount} layers to map', { layerCount: Object.keys(layerSpec).length });
	return layerSpec;
}

/**
 * Show or hide a single map layer by id.
 */
export function setLayerVisibility(map: MapLibreMap, id: string, visible: boolean): void {
	map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
}

// Keyed by layer id. Kept outside $state so paintSelected/paintInitial don't
// mutate reactive store values. `paint` is undefined when the style declared
// no explicit value — restore then clears the prop back to the spec default.
const savedPaintByLayerId = new Map<string, { prop: string; paint: unknown }[]>();

// Opacity props and dim factor per layer type while a selection is active; heatmap is absent because feature-state isn't supported in its paint.
const DIM_PAINT: Record<string, { props: string[]; dim: number }> = {
	line: { props: ['line-opacity'], dim: 0.3 },
	symbol: { props: ['text-opacity', 'icon-opacity'], dim: 0.3 },
	// Circles dim harder to keep dense crash points legible.
	circle: { props: ['circle-opacity'], dim: 0.2 },
	fill: { props: ['fill-opacity'], dim: 0.3 }
};

// Highlighted feature stays at full opacity; everything else is scaled by dim.
const HIGHLIGHT_TEST = ['boolean', ['feature-state', 'highlight'], false];

function dimOutput(value: unknown, dim: number): unknown {
	return ['case', HIGHLIGHT_TEST, 1, ['*', value, dim]];
}

/**
 * Derive the selection-dim opacity expression from a layer's base opacity.
 *
 * `["zoom"]` may only appear as the direct input to a top-level `step` /
 * `interpolate` — nesting it inside a `case` or `*` makes MapLibre reject the
 * whole expression (the paint is then silently dropped, so the layer never
 * dims). So for a zoom-driven base opacity we push the dim + highlight logic
 * into each *output* value, keeping `zoom` at the top; for constant or purely
 * data-driven (no-zoom) bases we wrap the whole value. Both forms are
 * equivalent: highlighted → 1, everything else → base × dim.
 */
function deriveDimPaint(current: unknown, dim: number): unknown {
	if (Array.isArray(current)) {
		const op = current[0];
		// ["step", input, out0, stop1, out1, …] — outputs at even indices ≥ 2.
		if (op === 'step') {
			return current.map((part, i) => (i >= 2 && i % 2 === 0 ? dimOutput(part, dim) : part));
		}
		// ["interpolate", interpolation, input, stop0, out0, …] — outputs at even indices ≥ 4.
		if (typeof op === 'string' && op.startsWith('interpolate')) {
			return current.map((part, i) => (i >= 4 && i % 2 === 0 ? dimOutput(part, dim) : part));
		}
	}
	return dimOutput(current, dim);
}

/**
 * Save the current paint value for each relevant opacity prop, then dim it:
 * the highlighted feature stays at full opacity and the rest are scaled by the
 * type's DIM_PAINT factor. The dim expression is derived from the layer's own base
 * opacity, so no per-layer on-selection style is needed. Idempotent — a second
 * call with saved state is a no-op.
 */
export function paintSelected(map: MapLibreMap, layerSource: ILayerSource): void {
	const id = layerSource.layer.id;
	if (!id || savedPaintByLayerId.has(id)) return;
	const dimPaint = DIM_PAINT[layerSource.layer.type ?? ''];
	if (!dimPaint) return;
	const { props, dim } = dimPaint;
	const saved: { prop: string; paint: unknown }[] = [];
	for (const prop of props) {
		const current = map.getPaintProperty(id, prop);
		saved.push({ prop, paint: current });
		// An unset opacity prop renders at the spec default (1), so dim from
		// that base — otherwise layers whose style JSON declares no explicit
		// opacity would never dim.
		map.setPaintProperty(id, prop, deriveDimPaint(current ?? 1, dim) as never);
	}
	savedPaintByLayerId.set(id, saved);
}

/**
 * Restore the paint prop saved by paintSelected. Idempotent — no-op if nothing
 * was saved.
 */
export function paintInitial(map: MapLibreMap, layerSource: ILayerSource): void {
	const id = layerSource.layer.id;
	if (!id) return;
	const saved = savedPaintByLayerId.get(id);
	if (!saved) return;
	for (const { prop, paint } of saved) {
		// `paint` is undefined for props the style never set — setPaintProperty
		// then clears the dim override, reverting to the spec default.
		map.setPaintProperty(id, prop, paint as never);
	}
	savedPaintByLayerId.delete(id);
}

/**
 * Ease the map (preserving zoom) so `feature`'s centre sits in the middle of
 * the map area left of the attributes panel. Panel compensation uses the
 * transient `offset` option, NOT easeTo `padding`: padding given to
 * easeTo/jumpTo persists on the viewport (`setPadding`), and cameraForBounds
 * adds persisted padding on top of its own `padding` option — so a padded ease
 * here would double-count the panel in every later fitFeatureBounds call.
 */
export function easeToFeature(
	map: MapLibreMap,
	feature: MapGeoJSONFeature,
	panelWidth: number
): void {
	const center = getCenter(feature);
	if (center) map.easeTo({ center, offset: [-panelWidth / 2, 0] });
}

/**
 * Cap for bounds-fitting zooms (street level). Point features (intersections,
 * signs, count sites) produce zero-area bounds, and an uncapped `fitBounds`
 * zooms a degenerate bbox all the way to the map's maximum zoom. Line/area
 * features are unaffected unless they're small enough that street level is
 * the right stopping point anyway.
 */
const MAX_FIT_BOUNDS_ZOOM = 15;

/** Base padding for feature-bounds fits (table "Show on map", panel "Zoom to"). */
const FIT_BOUNDS_PADDING = 60;

/**
 * Fit the map to `feature`'s bounds, reserving `panelWidth` px on the right for
 * the open attributes panel. The padding is transient (fitBounds), see
 * easeToFeature for why it must never go to easeTo/jumpTo.
 */
export function fitFeatureBounds(
	map: MapLibreMap,
	feature: MapGeoJSONFeature,
	panelWidth: number
): void {
	const bounds = getBounds(feature);
	const pad = FIT_BOUNDS_PADDING;
	if (bounds) {
		map.fitBounds(bounds, {
			maxZoom: MAX_FIT_BOUNDS_ZOOM,
			padding: { top: pad, bottom: pad, left: pad, right: pad + panelWidth },
			linear: true
		});
	}
}

/**
 * Query the given layer(s) for rendered features, then dedupe by `feature.id`
 * and concatenate LineString coordinates of segments that share an id.
 *
 * Vector tiles split features at tile boundaries, so a single road LineString
 * is returned as N features (one per tile it crosses). This helper rejoins
 * them so the table shows one row per real-world feature and the geometry
 * spans its full extent — important for "Show on map" zoom-to-bounds.
 *
 * Several layers may be queried at once (a table tab spanning sibling layers
 * that draw the same root feature) — the id dedupe then also collapses
 * features rendered by more than one of them.
 *
 * Coordinate concat is naive (duplicates between adjacent segments are kept);
 * the merged geometry is only used for bounding-box computation, so noise in
 * the coordinate list doesn't matter.
 */
export function queryRenderedFeaturesReduced(
	map: MapLibreMap,
	layerIds: string | string[]
): MapGeoJSONFeature[] {
	// queryRenderedFeatures throws on unknown layer ids — skip layers that
	// haven't been added (their source may have failed to build).
	const layers = (Array.isArray(layerIds) ? layerIds : [layerIds]).filter((id) => map.getLayer(id));
	if (!layers.length) return [];
	const feats = map.queryRenderedFeatures({ layers });
	// Map<id, accum index> — O(1) lookup vs the O(n) `ids.indexOf` it replaces.
	// Tile layers commonly produce thousands of feature fragments per query.
	const idIndex = new Map<number | string | undefined, number>();
	return feats.reduce<MapGeoJSONFeature[]>((accum, feat) => {
		const existing = idIndex.get(feat.id);
		if (existing === undefined) {
			idIndex.set(feat.id, accum.length);
			accum.push(feat);
		} else if (accum[existing].geometry.type === 'LineString') {
			const prevCoords = (accum[existing].geometry as LineString).coordinates;
			const thisCoords = (feat.geometry as LineString).coordinates;
			(accum[existing].geometry as LineString).coordinates = prevCoords.concat(thisCoords);
		}
		return accum;
	}, []);
}

// Label layers raised above the data, listed bottom-first: each is moved in
// turn, so the last id ends up topmost. Road labels come from the context roads
// style or the 'full' basemap; place / city labels from either basemap
// (`basemapStyleUrls`). Ids absent from a given style are skipped.
const LABEL_LAYER_IDS = ['Road labels', 'Route labels', 'Place labels', 'City labels'];

/**
 * Move the basemap labels to the top of the layer stack, or directly below
 * `anchorLayerId` for apps whose data symbols must draw over them (see
 * `getLabelsAnchorLayerId` on the app's MapConfig). Idempotent, so it can be
 * re-run whenever layers are added to keep labels on top.
 */
export function raiseMapLabels(map: MapLibreMap, anchorLayerId?: string): void {
	// An undefined beforeId moves the layer to the top of the stack.
	const beforeId = anchorLayerId && map.getLayer(anchorLayerId) ? anchorLayerId : undefined;
	for (const id of LABEL_LAYER_IDS) {
		if (map.getLayer(id)) map.moveLayer(id, beforeId);
	}
}

/**
 * Add the basemap roads context layer: vector source + "Road network" line layer
 * plus its road / route label symbol layers. Road network is moved below the
 * base map's "Path" layer so it renders as part of the basemap; label ordering
 * is left to raiseMapLabels.
 *
 * Basemap roads idempotent via a map.getSource() guard — safe to call from both
 * the initial load handler and a reactive $effect that fires when the context
 * layer finishes loading asynchronously.
 */
export function addContextLayers(map: MapLibreMap, contextLayer: TContextFeature): void {
	if (map.getSource(contextLayerId)) return;
	if (!contextLayer.xyzTemplate) {
		log.warn('Could not get roads context layer {id} - proceeding without basemap roads', {
			id: contextLayerId
		});
		return;
	}

	map.addSource(contextLayerId, {
		type: 'vector',
		encoding: 'mvt', // Note: As of June 2026 MLT can be served by Martin, but not yet consumed by MapLibre - see #12111
		tiles: [decodeURI(contextLayer.xyzTemplate)],
		maxzoom: 16
	});

	for (const layer of basemapRoadLayers) {
		map.addLayer(layer as AddLayerObject);
	}
	if (map.getLayer('Path')) {
		map.moveLayer('Road network', 'Path');
	}
}

/**
 * Add the satellite raster source and layer to the map, hidden initially.
 * Must be called BEFORE addMapLayers so data layers render on top of satellite.
 * Toggle visibility with setLayerVisibility(map, 'satellite', true/false).
 */
export const SATELLITE_LAYER_ID = 'satellite';

export function addSatelliteLayer(map: MapLibreMap, source: RasterSourceSpecification): void {
	if (map.getSource(SATELLITE_LAYER_ID)) return;
	map.addSource(SATELLITE_LAYER_ID, source);
	map.addLayer({
		id: SATELLITE_LAYER_ID,
		type: 'raster',
		source: SATELLITE_LAYER_ID,
		paint: {
			'raster-opacity': 1,
			'raster-contrast': -0.2,
			'raster-saturation': -0.9,
			'raster-brightness-min': 0.1
		}
	});
	setLayerVisibility(map, SATELLITE_LAYER_ID, false);
}

/**
 * Build a MapLibre filter expression from a legend layer's checked items.
 * Returns null when no filter should be applied (all items checked or no filterAttr).
 */
export function parseFilterFromLegend(layer: MapLayer): FilterSpecification | null {
	const clauses = layer.legendItems
		.filter((item) => item.enabled && item.filterValue)
		.map((item): ExpressionSpecification => ['==', ['get', layer.filterAttr], item.filterValue!]);
	if (!clauses.length) return null;
	return layer.initialFilter
		? ['all', layer.initialFilter as ExpressionSpecification, ['any', ...clauses]]
		: ['any', ...clauses];
}

/** One AI display-mode entry per layer: expression null = whole layer. */
type AgentFilterMap = Record<string, { expression: ExpressionSpecification | null }>;

// Layers whose map filter was last written by AI display mode. Needed so that
// when AI mode ends and the layer's own combined filter is null, we still call
// setFilter once more to reset it instead of leaving the stale agent filter.
const agentFilteredLayerIds = new Set<string>();

// Each agent-driven layer's own [minzoom, maxzoom], captured before AI display
// mode lowers the floor, so the original range is restored on exit.
const originalZoomRanges = new Map<string, [number, number]>();

/** AI display mode: show only agent-selected layers, filtered to the agent's expression. */
function syncAgentLayer(
	map: MapLibreMap,
	layer: MapLayer,
	agentEntry: AgentFilterMap[string] | undefined
): void {
	const { layerId } = layer;
	setLayerVisibility(map, layerId, Boolean(agentEntry));
	if (!agentEntry) return;
	map.setFilter(layerId, (agentEntry.expression as FilterSpecification) ?? null);
	if (!originalZoomRanges.has(layerId)) {
		const styleLayer = map.getLayer(layerId);
		originalZoomRanges.set(layerId, [
			styleLayer?.minzoom ?? layer.minzoom ?? 0,
			styleLayer?.maxzoom ?? 24
		]);
	}
	// Lower only the zoom floor: layers that hand over at a threshold (accidents heatmap to circles) must keep their ceiling.
	const [, originalMaxZoom] = originalZoomRanges.get(layerId) ?? [0, 24];
	map.setLayerZoomRange(layerId, 0, originalMaxZoom);
	agentFilteredLayerIds.add(layerId);
}

/** Manual mode: legend checkboxes plus the user's data filter, undoing any AI display-mode state. */
function syncManualLayer(
	map: MapLibreMap,
	layer: MapLayer,
	mapDataFilter: ExpressionSpecification | null
): void {
	const { layerId, layerAnyEnabled } = layer;
	setLayerVisibility(map, layerId, layerAnyEnabled);
	if (agentFilteredLayerIds.has(layerId)) {
		const [originalMinZoom, originalMaxZoom] = originalZoomRanges.get(layerId) ?? [
			layer.minzoom ?? 0,
			24
		];
		map.setLayerZoomRange(layerId, originalMinZoom, originalMaxZoom);
		originalZoomRanges.delete(layerId);
	}
	if (!layerAnyEnabled) return;

	const combined = buildCombinedFilter(
		parseFilterFromLegend(layer),
		layer.excludeFromMapDataFilter ? null : mapDataFilter
	);
	// A null combined filter after AI mode still needs an explicit reset so the stale agent filter doesn't linger.
	if (combined !== null || agentFilteredLayerIds.has(layerId)) {
		map.setFilter(layerId, combined);
		agentFilteredLayerIds.delete(layerId);
	}
}

/**
 * Sync all legend layers to the map (visibility and filter); call from a $effect on legend, data-filter or AI-mode changes.
 * In AI display mode only layers with an `agentFilters` entry show, and legend/manual state is left untouched for restore on exit.
 *
 * @param mapDataFilter - mapDataFilterStore expression, skipped for layers with `excludeFromMapDataFilter`; null for legend-only sync.
 * @param agentFilters - Per-layer AI display entries (agentFilterStore.layerFilters).
 * @param aiMode - Whether AI display mode is active (agentFilterStore.aiMode).
 */
export function syncVisibleLayers(
	map: MapLibreMap,
	layers: MapLayer[],
	mapDataFilter: ExpressionSpecification | null,
	agentFilters: AgentFilterMap,
	aiMode: boolean
): void {
	for (const layer of layers) {
		if (!layer?.layerId) continue;
		const { layerId } = layer;
		if (!map.getLayer(layerId)) {
			log.debug('syncVisibleLayers: {layerId} not on map — toggle has no effect', { layerId });
			continue;
		}
		if (aiMode) syncAgentLayer(map, layer, agentFilters[layerId]);
		else syncManualLayer(map, layer, mapDataFilter);
	}
}

function buildCombinedFilter(
	legendFilter: FilterSpecification | null,
	dataFilter: ExpressionSpecification | null
): FilterSpecification | null {
	return legendFilter && dataFilter
		? ['all', legendFilter as ExpressionSpecification, dataFilter]
		: (legendFilter ?? dataFilter);
}
